import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { ToggleSwitch } from '@/components/legacy-ui';
import { useGenerateHealthReport } from '@/hooks/useGenerateHealthReport';
import {
  ReportPreferences,
  useReportPreferences,
  validateRecipientDomain,
} from '@/hooks/useReportPreferences';
import { SettingRow } from '@/sections/settings/components';
import { useAuth } from '@/state/auth';
import { cn } from '@/utils/classname';

interface ReportPreferencesCardProps {
  /** Optional customer email for impersonation support */
  email?: string;
  /** Controls whether edit controls are shown */
  editable?: boolean;
}

const CADENCE_OPTIONS: {
  value: ReportPreferences['cadence'];
  label: string;
}[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

/**
 * ReportPreferencesCard — configures scheduled health report delivery.
 *
 * Row 1: Enable/disable toggle
 * Row 2: Cadence selector (weekly/monthly/quarterly) — visible when enabled
 * Row 3: Email recipients with add/remove — visible when enabled
 * Row 4: Save + Generate Now buttons — visible when enabled
 *
 * Email validation: recipients must share the same domain as the user's email.
 * Self-inclusion: user's email is always shown as the first recipient (non-removable).
 */
export function ReportPreferencesCard({
  email: customerEmail,
  editable = true,
}: ReportPreferencesCardProps) {
  const { me } = useAuth();
  const userEmail = me || '';

  const { preferences, isLoading, savePreferences, isSaving } =
    useReportPreferences(customerEmail);

  const { generate, isGenerating } = useGenerateHealthReport();

  // Local form state
  const [localEnabled, setLocalEnabled] = useState<boolean | undefined>(
    undefined
  );
  const [localCadence, setLocalCadence] = useState<
    ReportPreferences['cadence'] | undefined
  >(undefined);
  const [localRecipients, setLocalRecipients] = useState<string[] | undefined>(
    undefined
  );

  const [newRecipient, setNewRecipient] = useState('');
  const [recipientError, setRecipientError] = useState('');

  // Derive current display values (local overrides server state)
  const enabled = localEnabled ?? preferences.enabled;
  const cadence = localCadence ?? preferences.cadence;
  const recipients = localRecipients ?? preferences.recipients;

  const isDirty =
    localEnabled !== undefined ||
    localCadence !== undefined ||
    localRecipients !== undefined;

  function handleToggleEnabled(checked: boolean) {
    setLocalEnabled(checked);
  }

  function handleCadenceChange(value: ReportPreferences['cadence']) {
    setLocalCadence(value);
  }

  function handleAddRecipient() {
    const trimmed = newRecipient.trim().toLowerCase();

    if (!trimmed) return;

    // Basic email format check
    const [localPart, domain, ...rest] = trimmed.split('@');
    if (!localPart || !domain || rest.length > 0) {
      setRecipientError('Please enter a valid email address.');
      return;
    }

    // Domain validation
    if (userEmail && !validateRecipientDomain(trimmed, userEmail)) {
      const userDomain = userEmail.split('@')[1] || '';
      setRecipientError(
        `Recipients must be from the same domain (${userDomain})`
      );
      return;
    }

    // Uniqueness check (case-insensitive, also excludes own email duplicate)
    const allRecipients = userEmail
      ? [userEmail.toLowerCase(), ...recipients.map(r => r.toLowerCase())]
      : recipients.map(r => r.toLowerCase());
    if (allRecipients.includes(trimmed)) {
      setRecipientError('This recipient is already in the list.');
      return;
    }

    // Limit
    if (recipients.length >= 10) {
      setRecipientError('Maximum 10 recipients allowed.');
      return;
    }

    setLocalRecipients([...recipients, trimmed]);
    setNewRecipient('');
    setRecipientError('');
  }

  function handleRemoveRecipient(recipientToRemove: string) {
    setLocalRecipients(recipients.filter(r => r !== recipientToRemove));
  }

  async function handleSave() {
    const prefs: ReportPreferences = {
      enabled,
      cadence,
      recipients,
    };

    try {
      await savePreferences(prefs);
      // Reset dirty state after successful save
      setLocalEnabled(undefined);
      setLocalCadence(undefined);
      setLocalRecipients(undefined);
    } catch {
      // Error toast already shown by savePreferences in the hook
    }
  }

  if (isLoading) {
    return (
      <div className="text-chariot-text-secondary py-4 text-sm">
        Loading report preferences...
      </div>
    );
  }

  return (
    <div className="divide-chariot-stroke divide-y">
      {/* Row 1: Enable/Disable Toggle */}
      <SettingRow data-testid="report-enabled-row">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-chariot-text-primary text-sm font-medium">
              Scheduled Health Reports
            </span>
            <span className="text-chariot-text-secondary text-xs">
              Receive periodic security health report emails
            </span>
          </div>
          <ToggleSwitch
            id="report-enabled-toggle"
            checked={enabled}
            onChange={editable ? handleToggleEnabled : () => {}}
            disabled={!editable}
          />
        </div>
      </SettingRow>

      {/* Rows 2-3: Only visible when reports are enabled */}
      {enabled && (
        <>
          {/* Row 2: Cadence Selector */}
          <SettingRow data-testid="report-cadence-row">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <span className="text-chariot-text-primary text-sm font-medium">
                  Cadence
                </span>
                <span className="text-chariot-text-secondary text-xs">
                  How often you receive health report emails
                </span>
              </div>
              <select
                value={cadence}
                onChange={e =>
                  editable &&
                  handleCadenceChange(
                    e.target.value as ReportPreferences['cadence']
                  )
                }
                disabled={!editable}
                aria-label="Report cadence"
                className={cn(
                  'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary',
                  'rounded-md border px-3 py-1.5 text-sm',
                  'focus:ring-chariot-brand-primary focus:ring-1 focus:outline-hidden',
                  !editable && 'cursor-not-allowed opacity-50'
                )}
                data-testid="report-cadence-select"
              >
                {CADENCE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </SettingRow>

          {/* Row 3: Email Recipients */}
          <SettingRow data-testid="report-recipients-row">
            <div className="flex flex-col gap-3">
              <div>
                <span className="text-chariot-text-primary text-sm font-medium">
                  Recipients
                </span>
                <p className="text-chariot-text-secondary text-xs">
                  Reports will be sent to these email addresses
                </p>
              </div>

              {/* Current recipients list */}
              <div className="flex flex-col gap-2">
                {/* User's own email — always shown, non-removable */}
                {userEmail && (
                  <div className="flex items-center justify-between">
                    <span className="text-chariot-text-primary text-sm">
                      {userEmail}
                    </span>
                    <span className="text-chariot-text-secondary text-xs italic">
                      (you)
                    </span>
                  </div>
                )}

                {/* Additional recipients */}
                {recipients.map(recipient => (
                  <div
                    key={recipient}
                    className="flex items-center justify-between"
                  >
                    <span className="text-chariot-text-primary text-sm">
                      {recipient}
                    </span>
                    {editable && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRecipient(recipient)}
                        className="text-chariot-text-secondary hover:text-chariot-text-primary text-xs"
                        aria-label={`Remove ${recipient}`}
                        data-testid={`remove-recipient-${recipient}`}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Add recipient input */}
              {editable && (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <input
                      type="email"
                      value={newRecipient}
                      onChange={e => {
                        setNewRecipient(e.target.value);
                        setRecipientError('');
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddRecipient();
                        }
                      }}
                      placeholder="Add recipient email..."
                      aria-label="Add recipient email address"
                      className={cn(
                        'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary',
                        'flex-1 rounded-md border px-3 py-1.5 text-sm',
                        'placeholder:text-chariot-text-secondary',
                        'focus:ring-chariot-brand-primary focus:ring-1 focus:outline-hidden'
                      )}
                      data-testid="add-recipient-input"
                    />
                    <Button
                      variant="outline"
                      onClick={handleAddRecipient}
                      disabled={!newRecipient.trim()}
                      data-testid="add-recipient-button"
                    >
                      Add
                    </Button>
                  </div>
                  {recipientError && (
                    <p
                      className="text-xs text-red-500"
                      data-testid="recipient-error"
                    >
                      {recipientError}
                    </p>
                  )}
                </div>
              )}
            </div>
          </SettingRow>
        </>
      )}

      {/* Row 4: Actions — always visible when form is dirty */}
      {editable && isDirty && (
        <SettingRow data-testid="report-actions-row">
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              data-testid="save-report-preferences-button"
            >
              {isSaving ? 'Saving...' : 'Save Preferences'}
            </Button>
            {enabled && (
              <Button
                variant="outline"
                onClick={() => generate()}
                disabled={isGenerating}
                data-testid="generate-health-report-button"
              >
                {isGenerating ? 'Generating...' : 'Generate Report Now'}
              </Button>
            )}
          </div>
        </SettingRow>
      )}
    </div>
  );
}
